"use client";

import { useState, useMemo, useCallback, useEffect } from "react";
import { ArrowLeft, CheckCircle2, XCircle, Lightbulb, Loader2, EyeOff, Star, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SpeakerButton } from "@/components/speaker-button";
import { useTTS } from "@/lib/use-tts";
import { useTranslations } from "@/lib/use-translations";
import { useLanguage } from "@/lib/language-context";
import type { TopicVocabulary, WordEntry } from "@/lib/dialog-data";
import type { LocaleCode } from "@/lib/api/client";
import type { useWordState } from "@/lib/use-word-state";

type ExerciseType = "introduce" | "compose" | "describe" | "fill_blank";
type QuizState = "active" | "correct" | "incorrect" | "complete";

interface QuizStep {
  wordIndex: number;
  exercise: ExerciseType;
}

interface WordQuizProps {
  vocabulary: TopicVocabulary;
  language: LocaleCode;
  topicTitle?: string;
  topicDescription?: string;
  onBack: () => void;
  words?: WordEntry[];
  wordState?: ReturnType<typeof useWordState>;
}

function shuffleArray<T>(arr: T[]): T[] {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!];
  }
  return shuffled;
}

function seededShuffle<T>(arr: T[], rand: () => number): T[] {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!];
  }
  return shuffled;
}

/** Find a sentence containing a specific form of the word, returning the form and sentence. */
function findSentenceWithForm(word: WordEntry): { form: string; sentence: string; translation: string } | null {
  // Collect all forms (base form + conjugated forms)
  const allForms = [word.baseForm, ...word.forms.map((f) => f.form)];

  // 1. Search form examples and context lines first (most reliable — from dialogue)
  for (const formEntry of word.forms) {
    const formWord = formEntry.form;
    if (formEntry.example?.sentence) {
      const regex = new RegExp(`\\b${formWord.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i");
      if (regex.test(formEntry.example.sentence)) {
        return { form: formWord, sentence: formEntry.example.sentence, translation: formEntry.example.translation || "" };
      }
    }
    if (formEntry.context?.line) {
      const regex = new RegExp(`\\b${formWord.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i");
      if (regex.test(formEntry.context.line)) {
        return { form: formWord, sentence: formEntry.context.line, translation: formEntry.context.situation || "" };
      }
    }
  }

  // 2. Search word.examples for any form
  for (const example of word.examples) {
    for (const form of allForms) {
      const regex = new RegExp(`\\b${form.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i");
      if (regex.test(example.text)) {
        return { form, sentence: example.text, translation: example.translation };
      }
    }
  }

  return null;
}

/** Simple seeded PRNG (mulberry32). */
function seededRandom(seed: number): () => number {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Build a phased exercise sequence: introduce → recognition → compose. */
function buildExerciseSequence(words: WordEntry[], seed: number): QuizStep[] {
  const rand = seededRandom(seed);
  const steps: QuizStep[] = [];
  const indices = words.map((_, i) => i);

  // Phase 1: Introduce all words
  for (const i of indices) {
    steps.push({ wordIndex: i, exercise: "introduce" });
  }

  // Phase 2: Recognition (describe or fill_blank) in shuffled order
  for (const i of seededShuffle(indices, rand)) {
    const word = words[i]!;
    const options: ExerciseType[] = [];
    if (words.length >= 2) {
      if (word.illustratable) options.push("describe");
      if (findSentenceWithForm(word)) options.push("fill_blank");
    }
    if (options.length > 0) {
      steps.push({ wordIndex: i, exercise: options[Math.floor(rand() * options.length)]! });
    }
  }

  // Phase 3: Compose in shuffled order
  for (const i of seededShuffle(indices, rand)) {
    if (words[i]!.baseForm.length >= 2) {
      steps.push({ wordIndex: i, exercise: "compose" });
    }
  }

  return steps;
}

function getDistractors(currentWord: WordEntry, allWords: WordEntry[], count = 3): string[] {
  const others = allWords
    .filter((w) => w.baseForm !== currentWord.baseForm)
    .map((w) => w.baseForm);
  return shuffleArray(others).slice(0, count);
}

// --- Image prefetch cache (module-level, deduplicates concurrent fetches) ---
const _imageFetchPromises = new Map<string, Promise<string | null>>();
const _imageUrlCache = new Map<string, string>();

function _imageCacheKey(translation: string): string {
  return translation.trim().toLowerCase();
}

function fetchVocabImage(word: string, translation: string, language: LocaleCode, context?: string, partOfSpeech?: string): Promise<string | null> {
  const key = _imageCacheKey(translation);
  const existing = _imageFetchPromises.get(key);
  if (existing) return existing;

  const params = new URLSearchParams({ word, lang: language, translation });
  if (context) params.set("context", context);
  if (partOfSpeech) params.set("pos", partOfSpeech);

  const base = process.env.NEXT_PUBLIC_API_URL || "";
  const promise = fetch(`${base}/api/vocab-image?${params}`)
    .then((res) => {
      if (!res.ok) throw new Error("Failed to load image");
      return res.blob();
    })
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      _imageUrlCache.set(key, url);
      return url;
    })
    .catch(() => null);

  _imageFetchPromises.set(key, promise);
  return promise;
}

/** Preload images for upcoming steps that will show images. */
function preloadImages(
  words: WordEntry[],
  steps: QuizStep[],
  startIdx: number,
  count: number,
  language: LocaleCode,
  context?: string,
) {
  const imageTypes: ExerciseType[] = ["introduce", "compose", "describe"];
  let loaded = 0;
  for (let i = startIdx; i < steps.length && loaded < count; i++) {
    const step = steps[i]!;
    const word = words[step.wordIndex];
    if (word && imageTypes.includes(step.exercise) && word.illustratable) {
      fetchVocabImage(word.baseForm, word.translation, language, context, word.partOfSpeech);
      loaded++;
    }
  }
}

// --- Image component with loading ---
function VocabImage({ word, translation, language, context, partOfSpeech }: { word: string; translation: string; language: LocaleCode; context?: string; partOfSpeech?: string }) {
  const cachedUrl = _imageUrlCache.get(_imageCacheKey(translation));
  const [imgUrl, setImgUrl] = useState<string | null>(cachedUrl || null);
  const [loading, setLoading] = useState(!cachedUrl);
  const [error, setError] = useState(false);

  useEffect(() => {
    const cached = _imageUrlCache.get(_imageCacheKey(translation));
    if (cached) {
      setImgUrl(cached);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(false);

    fetchVocabImage(word, translation, language, context, partOfSpeech).then((url) => {
      if (cancelled) return;
      if (url) {
        setImgUrl(url);
        setLoading(false);
      } else {
        setLoading(false);
        setError(true);
      }
    });

    return () => { cancelled = true; };
  }, [word, translation, language, context]);

  if (loading) {
    return (
      <div className="w-48 h-48 rounded-xl bg-muted animate-pulse flex items-center justify-center mx-auto">
        <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
      </div>
    );
  }

  if (error || !imgUrl) {
    return (
      <div className="w-48 h-48 rounded-xl bg-muted/50 flex items-center justify-center mx-auto">
        <span className="text-4xl">{word.slice(0, 2)}</span>
      </div>
    );
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={imgUrl}
      alt={word}
      className="w-48 h-48 rounded-xl object-cover mx-auto"
    />
  );
}

// --- Word state action buttons (shown on feedback screens) ---
function WordStateActions({
  word,
  wordState,
}: {
  word: WordEntry;
  wordState?: ReturnType<typeof useWordState>;
}) {
  const t = useTranslations();
  if (!wordState) return null;

  const known = wordState.isKnown(word.baseForm);
  const tough = wordState.isTough(word.baseForm);
  const liked = wordState.isLiked(word.baseForm);

  return (
    <div className="flex justify-center gap-3 pt-1">
      <button
        onClick={() => wordState.toggleKnown(word.baseForm)}
        className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs transition-all border ${
          known
            ? "bg-slate-500/20 border-slate-500/40 text-slate-600 dark:text-slate-400"
            : "bg-muted/50 border-border/50 text-muted-foreground hover:bg-muted"
        }`}
        title={t.learning.markKnown || "I know this"}
      >
        <EyeOff className="w-3.5 h-3.5" />
        <span>{t.learning.markKnown || "I know this"}</span>
      </button>
      <button
        onClick={() => wordState.toggleTough(word.baseForm)}
        className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs transition-all border ${
          tough
            ? "bg-amber-500/20 border-amber-500/40 text-amber-600 dark:text-amber-400"
            : "bg-muted/50 border-border/50 text-muted-foreground hover:bg-muted"
        }`}
        title={t.learning.markTough || "Tough one"}
      >
        <Star className="w-3.5 h-3.5" />
        <span>{t.learning.markTough || "Tough one"}</span>
      </button>
      <button
        onClick={() => wordState.toggleLiked(word.baseForm)}
        className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs transition-all border ${
          liked
            ? "bg-rose-500/20 border-rose-500/40 text-rose-600 dark:text-rose-400"
            : "bg-muted/50 border-border/50 text-muted-foreground hover:bg-muted"
        }`}
        title={t.learning.markLiked || "Like"}
      >
        <Heart className="w-3.5 h-3.5" />
        <span>{t.learning.markLiked || "Like"}</span>
      </button>
    </div>
  );
}

// --- Introduce exercise ---
function IntroduceExercise({
  word,
  language,
  topicContext,
  showImage,
  onNext,
}: {
  word: WordEntry;
  language: LocaleCode;
  topicContext?: string;
  showImage: boolean;
  onNext: () => void;
}) {
  const tts = useTTS({ lang: language });
  const t = useTranslations();
  const example = word.examples[0];
  const [lastSpoken, setLastSpoken] = useState(word.baseForm);

  const handleSpeak = useCallback((text: string) => {
    setLastSpoken(text);
    tts.speak(text);
  }, [tts]);

  const getStateForText = useCallback((text: string): "idle" | "loading" | "playing" =>
    lastSpoken === text ? tts.state : "idle", [lastSpoken, tts.state]);

  // Auto-play pronunciation when word appears
  useEffect(() => {
    setLastSpoken(word.baseForm);
    tts.speak(word.baseForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [word.baseForm]);

  return (
    <div className="space-y-4 text-center">
      <p className="text-sm text-muted-foreground font-medium">
        {t.learning.introduceWord || "New word"}
      </p>
      {showImage && (
        <VocabImage word={word.baseForm} translation={word.translation} language={language} context={topicContext} partOfSpeech={word.partOfSpeech} />
      )}
      <div className="text-2xl font-bold text-foreground">{word.baseForm}</div>
      {word.vocalizationTip && (
        <p className="text-sm text-primary/80 font-medium">{word.vocalizationTip}</p>
      )}
      <p className="text-base text-muted-foreground">{word.translation}</p>
      <div className="flex justify-center">
        <SpeakerButton state={getStateForText(word.baseForm)} onClick={() => handleSpeak(word.baseForm)} size="lg" />
      </div>
      {example && (
        <div className="p-3 rounded-xl bg-muted/30 border border-border/50 text-left">
          <div className="flex items-start gap-2">
            <div className="flex-1">
              <p className="text-sm text-foreground">{example.text}</p>
              <p className="text-xs text-muted-foreground mt-1">{example.translation}</p>
            </div>
            <SpeakerButton
              state={getStateForText(example.text)}
              onClick={() => handleSpeak(example.text)}
              size="sm"
              className="shrink-0 mt-0.5"
            />
          </div>
        </div>
      )}
      <Button onClick={onNext} className="w-full">
        {t.learning.nextWord || "Next"}
      </Button>
    </div>
  );
}

// --- Compose exercise ---
function ComposeExercise({
  word,
  language,
  topicContext,
  showImage,
  onCorrect,
  onIncorrect,
  onDontKnow,
}: {
  word: WordEntry;
  language: LocaleCode;
  topicContext?: string;
  showImage: boolean;
  onCorrect: () => void;
  onIncorrect: () => void;
  onDontKnow: () => void;
}) {
  const letters = useMemo(() => shuffleArray(word.baseForm.split("")), [word.baseForm]);
  const [placed, setPlaced] = useState<string[]>([]);
  const [available, setAvailable] = useState<{ letter: string; idx: number }[]>(
    () => letters.map((l, i) => ({ letter: l, idx: i }))
  );
  const [hintUsed, setHintUsed] = useState(false);
  const t = useTranslations();

  // Reset when word changes
  useEffect(() => {
    const shuffled = shuffleArray(word.baseForm.split(""));
    setPlaced([]);
    setAvailable(shuffled.map((l, i) => ({ letter: l, idx: i })));
    setHintUsed(false);
  }, [word.baseForm]);

  const handleLetterTap = (item: { letter: string; idx: number }) => {
    const newPlaced = [...placed, item.letter];
    setPlaced(newPlaced);
    setAvailable((prev) => prev.filter((a) => a.idx !== item.idx));

    if (newPlaced.length === word.baseForm.length) {
      if (newPlaced.join("") === word.baseForm) {
        setTimeout(onCorrect, 300);
      } else {
        setTimeout(onIncorrect, 300);
      }
    }
  };

  const handleHint = () => {
    setHintUsed(true);
    // Reveal the next correct letter
    const nextIdx = placed.length;
    const nextLetter = word.baseForm[nextIdx];
    const availableItem = available.find((a) => a.letter === nextLetter);
    if (availableItem) {
      handleLetterTap(availableItem);
    }
  };

  const handleUndo = () => {
    if (placed.length === 0) return;
    const lastLetter = placed[placed.length - 1]!;
    setPlaced((prev) => prev.slice(0, -1));
    setAvailable((prev) => [...prev, { letter: lastLetter, idx: Date.now() }]);
  };

  return (
    <div className="space-y-4 text-center">
      <p className="text-sm text-muted-foreground font-medium">
        {t.learning.composeWord || "Compose a word"}
      </p>
      {showImage ? (
        <VocabImage word={word.baseForm} translation={word.translation} language={language} context={topicContext} partOfSpeech={word.partOfSpeech} />
      ) : (
        <p className="text-lg text-muted-foreground">{word.translation}</p>
      )}

      {/* Input field */}
      <div
        className="flex justify-center gap-1 min-h-[48px] items-center cursor-pointer"
        onClick={handleUndo}
      >
        {word.baseForm.split("").map((_, idx) => (
          <div
            key={idx}
            className={`
              w-10 h-10 rounded-lg border-2 flex items-center justify-center text-lg font-bold
              ${idx < placed.length
                ? "border-primary bg-primary/10 text-primary"
                : "border-border bg-muted/30 text-transparent"
              }
            `}
          >
            {placed[idx] || "_"}
          </div>
        ))}
      </div>

      {/* Letter buttons */}
      <div className="flex flex-wrap justify-center gap-2">
        {available.map((item) => (
          <Button
            key={item.idx}
            variant="outline"
            size="lg"
            onClick={() => handleLetterTap(item)}
            className="w-12 h-12 text-lg font-bold"
          >
            {item.letter}
          </Button>
        ))}
      </div>

      <div className="flex justify-center gap-3">
        {!hintUsed && (
          <Button variant="ghost" size="sm" onClick={handleHint}>
            <Lightbulb className="w-4 h-4 mr-1" />
            {t.learning.iNeedAHint || "I need a Hint"}
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={onDontKnow} className="text-muted-foreground">
          {t.learning.iDontKnow || "I don't know this word"}
        </Button>
      </div>
    </div>
  );
}

// --- Describe (multiple choice) exercise ---
function DescribeExercise({
  word,
  allWords,
  language,
  topicContext,
  onCorrect,
  onIncorrect,
}: {
  word: WordEntry;
  allWords: WordEntry[];
  language: LocaleCode;
  topicContext?: string;
  onCorrect: () => void;
  onIncorrect: () => void;
}) {
  const options = useMemo(() => {
    const distractors = getDistractors(word, allWords);
    return shuffleArray([word.baseForm, ...distractors]);
  }, [word, allWords]);
  const [selected, setSelected] = useState<string | null>(null);
  const [hintUsed, setHintUsed] = useState(false);
  const [eliminated, setEliminated] = useState<Set<string>>(new Set());
  const t = useTranslations();

  const handleSelect = (option: string) => {
    if (selected) return;
    setSelected(option);
    if (option === word.baseForm) {
      setTimeout(onCorrect, 600);
    } else {
      setTimeout(onIncorrect, 600);
    }
  };

  const handleHint = () => {
    setHintUsed(true);
    // Eliminate 2 wrong answers
    const wrong = options.filter((o) => o !== word.baseForm);
    const toEliminate = shuffleArray(wrong).slice(0, 2);
    setEliminated(new Set(toEliminate));
  };

  return (
    <div className="space-y-4 text-center">
      <p className="text-sm text-muted-foreground font-medium">
        {t.learning.describeImage || "Describe what's on a picture"}
      </p>
      <VocabImage word={word.baseForm} translation={word.translation} language={language} context={topicContext} partOfSpeech={word.partOfSpeech} />

      <div className="grid grid-cols-2 gap-2">
        {options.map((option) => {
          const isEliminated = eliminated.has(option);
          const isSelected = selected === option;
          const isCorrect = option === word.baseForm;

          return (
            <Button
              key={option}
              variant="outline"
              onClick={() => handleSelect(option)}
              disabled={!!selected || isEliminated}
              className={`
                h-12 text-base transition-all
                ${isEliminated ? "opacity-30" : ""}
                ${isSelected && isCorrect ? "border-emerald-500 bg-emerald-500/10 text-emerald-600" : ""}
                ${isSelected && !isCorrect ? "border-red-500 bg-red-500/10 text-red-600" : ""}
                ${!isSelected && selected && isCorrect ? "border-emerald-500 bg-emerald-500/10" : ""}
              `}
            >
              {option}
            </Button>
          );
        })}
      </div>

      {!hintUsed && !selected && (
        <Button variant="ghost" size="sm" onClick={handleHint}>
          <Lightbulb className="w-4 h-4 mr-1" />
          {t.learning.iNeedAHint || "I need a Hint"}
        </Button>
      )}
    </div>
  );
}

/** Get distractor forms from other words (picks random forms, not just base forms). */
function getFormDistractors(correctForm: string, currentWord: WordEntry, allWords: WordEntry[], count = 3): string[] {
  const otherForms: string[] = [];
  for (const w of allWords) {
    if (w.baseForm === currentWord.baseForm) continue;
    otherForms.push(w.baseForm);
    for (const f of w.forms) {
      otherForms.push(f.form);
    }
  }
  // Deduplicate and exclude the correct form
  const unique = [...new Set(otherForms)].filter((f) => f.toLowerCase() !== correctForm.toLowerCase());
  return shuffleArray(unique).slice(0, count);
}

// --- Fill in the blank exercise ---
function FillBlankExercise({
  word,
  allWords,
  onCorrect,
  onIncorrect,
}: {
  word: WordEntry;
  allWords: WordEntry[];
  onCorrect: () => void;
  onIncorrect: () => void;
}) {
  const match = useMemo(() => findSentenceWithForm(word), [word]);
  const correctForm = match!.form;
  const fullSentence = match!.sentence;
  const sentenceTranslation = match!.translation;

  // Mask the form in the sentence
  const maskedSentence = fullSentence.replace(
    new RegExp(`\\b${correctForm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i"),
    "______"
  );

  const options = useMemo(() => {
    const distractors = getFormDistractors(correctForm, word, allWords);
    return shuffleArray([correctForm, ...distractors]);
  }, [correctForm, word, allWords]);
  const [selected, setSelected] = useState<string | null>(null);
  const [hintUsed, setHintUsed] = useState(false);
  const [eliminated, setEliminated] = useState<Set<string>>(new Set());
  const t = useTranslations();

  const handleSelect = (option: string) => {
    if (selected) return;
    setSelected(option);
    if (option === correctForm) {
      setTimeout(onCorrect, 600);
    } else {
      setTimeout(onIncorrect, 600);
    }
  };

  const handleHint = () => {
    setHintUsed(true);
    const wrong = options.filter((o) => o !== correctForm);
    const toEliminate = shuffleArray(wrong).slice(0, 2);
    setEliminated(new Set(toEliminate));
  };

  return (
    <div className="space-y-4 text-center">
      <p className="text-sm text-muted-foreground font-medium">
        {t.learning.pickWordForSentence || "Pick a word for the sentence"}
      </p>

      <div className="p-4 rounded-xl bg-muted/30 border border-border/50">
        <p className="text-lg text-foreground">
          {selected === correctForm ? fullSentence : maskedSentence}
        </p>
        {sentenceTranslation && (
          <p className="text-sm text-muted-foreground mt-2">{sentenceTranslation}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        {options.map((option) => {
          const isEliminated = eliminated.has(option);
          const isSelected = selected === option;
          const isCorrect = option === correctForm;

          return (
            <Button
              key={option}
              variant="outline"
              onClick={() => handleSelect(option)}
              disabled={!!selected || isEliminated}
              className={`
                h-12 text-base transition-all
                ${isEliminated ? "opacity-30" : ""}
                ${isSelected && isCorrect ? "border-emerald-500 bg-emerald-500/10 text-emerald-600" : ""}
                ${isSelected && !isCorrect ? "border-red-500 bg-red-500/10 text-red-600" : ""}
                ${!isSelected && selected && isCorrect ? "border-emerald-500 bg-emerald-500/10" : ""}
              `}
            >
              {option}
            </Button>
          );
        })}
      </div>

      {!hintUsed && !selected && (
        <Button variant="ghost" size="sm" onClick={handleHint}>
          <Lightbulb className="w-4 h-4 mr-1" />
          {t.learning.iNeedAHint || "I need a Hint"}
        </Button>
      )}
    </div>
  );
}

// --- Main WordQuiz component ---
export function WordQuiz({ vocabulary, language, topicTitle, topicDescription, onBack, words: moduleWords, wordState }: WordQuizProps) {
  const words = moduleWords || vocabulary.words;
  const topicContext = [topicTitle, topicDescription].filter(Boolean).join(" - ") || undefined;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [quizState, setQuizState] = useState<QuizState>("active");
  const [seed, setSeed] = useState(() => Date.now());
  // When true, show introduce card instead of compose for current word
  const [composeIntro, setComposeIntro] = useState(false);
  // Track incorrect words for tough-word repetition at end
  const [incorrectWords, setIncorrectWords] = useState<Set<number>>(new Set());
  const [inToughRound, setInToughRound] = useState(false);
  const [toughWords, setToughWords] = useState<WordEntry[]>([]);
  const [toughIndex, setToughIndex] = useState(0);

  const steps = useMemo(() => buildExerciseSequence(words, seed), [words, seed]);
  const quizStepCount = useMemo(() => steps.filter(s => s.exercise !== "introduce").length, [steps]);
  const currentStep = steps[currentIndex];
  const exerciseType = inToughRound ? "introduce" as const : currentStep?.exercise || "introduce";
  const t = useTranslations();
  const { targetLanguage } = useLanguage();
  const langCode = targetLanguage.code;

  // Preload first 5 images on mount / after seed change (restart)
  useEffect(() => {
    preloadImages(words, steps, 0, 5, langCode, topicContext);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed]);

  // Progressive preload as user advances — stay 3 images ahead
  useEffect(() => {
    preloadImages(words, steps, currentIndex + 1, 3, langCode, topicContext);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  // When quiz completes, preload all noun images for potential restart
  useEffect(() => {
    if (quizState === "complete") {
      for (const w of words) {
        if (w.illustratable && !_imageUrlCache.has(_imageCacheKey(w.translation))) {
          fetchVocabImage(w.baseForm, w.translation, langCode, topicContext, w.partOfSpeech);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizState]);

  const currentWord = inToughRound ? toughWords[toughIndex] : words[currentStep?.wordIndex ?? 0];
  const showImage = currentWord?.illustratable ?? false;

  const handleCorrect = useCallback(() => {
    setCorrectCount((c) => c + 1);
    setQuizState("correct");
  }, []);

  const handleIncorrect = useCallback(() => {
    if (!inToughRound && currentStep) {
      setIncorrectWords((prev) => new Set(prev).add(currentStep.wordIndex));
    }
    setQuizState("incorrect");
  }, [currentStep, inToughRound]);

  const handleDontKnow = useCallback(() => {
    if (!inToughRound && currentStep) {
      setIncorrectWords((prev) => new Set(prev).add(currentStep.wordIndex));
    }
    setComposeIntro(true);
  }, [currentStep, inToughRound]);

  const handleNext = useCallback(() => {
    setComposeIntro(false);
    if (inToughRound) {
      if (toughIndex + 1 >= toughWords.length) {
        setQuizState("complete");
      } else {
        setToughIndex(toughIndex + 1);
        setQuizState("active");
      }
      return;
    }

    if (currentIndex + 1 >= steps.length) {
      // Check for tough words that need repetition (incorrect + marked tough)
      const toughToRepeat: WordEntry[] = [];
      for (const idx of incorrectWords) {
        const w = words[idx];
        if (w && wordState?.isTough(w.baseForm)) {
          toughToRepeat.push(w);
        }
      }
      if (toughToRepeat.length > 0) {
        setInToughRound(true);
        setToughWords(toughToRepeat);
        setToughIndex(0);
        setQuizState("active");
      } else {
        setQuizState("complete");
      }
    } else {
      setCurrentIndex(currentIndex + 1);
      setQuizState("active");
    }
  }, [currentIndex, steps.length, words, inToughRound, toughIndex, toughWords.length, incorrectWords, wordState]);

  const handleRestart = () => {
    setCurrentIndex(0);
    setCorrectCount(0);
    setSeed(Date.now());
    setQuizState("active");
    setComposeIntro(false);
    setIncorrectWords(new Set());
    setInToughRound(false);
    setToughWords([]);
    setToughIndex(0);
  };

  if (words.length === 0) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        No words to practice
      </div>
    );
  }

  // Complete screen
  if (quizState === "complete") {
    return (
      <div className="p-4 space-y-6 max-w-lg mx-auto">
        <Button variant="ghost" size="icon" onClick={onBack} className="rounded-xl">
          <ArrowLeft className="w-5 h-5" />
        </Button>

        <div className="text-center space-y-4 pt-8">
          <CheckCircle2 className="w-16 h-16 text-blue-500 mx-auto" />
          <h2 className="text-2xl font-bold">{t.learning.practiceComplete || "Practice Complete!"}</h2>
          <p className="text-lg text-muted-foreground">
            {(t.learning.wordsCorrectCount || "{correct} of {total} correct")
              .replace("{correct}", String(correctCount))
              .replace("{total}", String(quizStepCount))}
          </p>

          <div className="flex gap-3 justify-center pt-4">
            <Button variant="outline" onClick={handleRestart}>
              {t.learning.practiceAgain || "Practice Again"}
            </Button>
            <Button onClick={onBack}>
              {t.learning.done || "Done"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!currentWord) return null;

  const displayIndex = inToughRound ? steps.length + toughIndex : currentIndex;
  const displayTotal = inToughRound ? steps.length + toughWords.length : steps.length;

  return (
    <div className="p-4 space-y-6 max-w-lg mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} className="rounded-xl">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h3 className="text-sm font-medium text-muted-foreground">
            {t.learning.practiceWords || "Words"}
          </h3>
          <div className="text-xs text-muted-foreground">
            {displayIndex + 1} / {displayTotal}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full bg-blue-500 rounded-full transition-all duration-300"
          style={{ width: `${((displayIndex) / displayTotal) * 100}%` }}
        />
      </div>

      {/* Exercise or feedback */}
      {quizState === "correct" && (
        <div className="text-center space-y-4 animate-fade-up">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
          <p className="text-lg font-medium text-emerald-600 dark:text-emerald-400">
            {t.learning.correct || "Correct!"}
          </p>
          <p className="text-sm text-muted-foreground">
            {currentWord.baseForm} — {currentWord.translation}
          </p>
          {currentWord.vocalizationTip && (
            <p className="text-xs text-primary/80">{currentWord.vocalizationTip}</p>
          )}
          <WordStateActions word={currentWord} wordState={wordState} />
          <Button onClick={handleNext} className="w-full">
            {t.learning.nextWord || "Next"}
          </Button>
        </div>
      )}

      {quizState === "incorrect" && (
        <div className="text-center space-y-4 animate-fade-up">
          <XCircle className="w-12 h-12 text-red-500 mx-auto" />
          <p className="text-sm text-muted-foreground">
            {currentWord.baseForm} — {currentWord.translation}
          </p>
          {currentWord.vocalizationTip && (
            <p className="text-xs text-primary/80">{currentWord.vocalizationTip}</p>
          )}
          <WordStateActions word={currentWord} wordState={wordState} />
          <Button onClick={handleNext} className="w-full">
            {t.learning.nextWord || "Next"}
          </Button>
        </div>
      )}

      {quizState === "active" && exerciseType === "introduce" && (
        <IntroduceExercise
          word={currentWord}
          language={langCode}
          topicContext={topicContext}
          showImage={showImage}
          onNext={handleNext}
        />
      )}

      {quizState === "active" && exerciseType === "compose" && !composeIntro && (
        <ComposeExercise
          word={currentWord}
          language={langCode}
          topicContext={topicContext}
          showImage={showImage}
          onCorrect={handleCorrect}
          onIncorrect={handleIncorrect}
          onDontKnow={handleDontKnow}
        />
      )}

      {quizState === "active" && exerciseType === "compose" && composeIntro && (
        <IntroduceExercise
          word={currentWord}
          language={langCode}
          topicContext={topicContext}
          showImage={showImage}
          onNext={handleNext}
        />
      )}

      {quizState === "active" && exerciseType === "describe" && (
        <DescribeExercise
          word={currentWord}
          allWords={words}
          language={langCode}
          topicContext={topicContext}
          onCorrect={handleCorrect}
          onIncorrect={handleIncorrect}
        />
      )}

      {quizState === "active" && exerciseType === "fill_blank" && (
        <FillBlankExercise
          word={currentWord}
          allWords={words}
          onCorrect={handleCorrect}
          onIncorrect={handleIncorrect}
        />
      )}
    </div>
  );
}
